import React, { useEffect } from "react";
import { withStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { Box, Paper, Tab, Tabs, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./DownloadCentreView.styles";
import * as DownloadCentreFileAction from "../../../stores/downloadCentreFiles/DownloadCentreFileAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import GetFileIcon from "@material-ui/icons/GetApp";
import DownloadCentreFileModel from "../../../models/downloadCentreFile/DownloadCentreFileModel";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`download-centre-tabpanel-${index}`}
            aria-labelledby={`download-centre-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={0}>{children}</Box>}
        </div>
    );
}

export interface IDownloadCentreViewProps extends WithStyles<typeof styles> {}

const DownloadCentreViewPure: React.FunctionComponent<IDownloadCentreViewProps> = ({
    classes
}: IDownloadCentreViewProps) => {
    const { t } = useTranslation();
    const [value, setValue] = React.useState(0);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(DownloadCentreFileAction.requestAllFiles());
    }, [dispatch]);

    const isRequestingDocumentation = useSelector(state => selectRequesting(state, [DownloadCentreFileAction.REQUEST_ALL_FILES]));

    const groupByCategory = (files) => {
        return files?.reduce((r, a) => {
            r[a.category] = [...r[a.category] || [], a];
            return r;
        }, {})
    }

    let filesFromState: DownloadCentreFileModel[] = Object.values(useSelector(state => state.donwloadCentreFiles));

    let groupedFiles: IUniversalTableData = groupByCategory(filesFromState.map((file: DownloadCentreFileModel) => ({
        name: file.name,
        category: file.category,
        type: file.type,
        size: file.size,
        link: file.link
    })));

    const tabs = [
        {
            title: t("manageAccount.downloadCentre.general"),
            field: "general"
        },
        {
            title: t("manageAccount.downloadCentre.binaries"),
            field: "binaries"
        },
        {
            title: t("manageAccount.downloadCentre.dataDocumentation"),
            field: "data-descriptions"
        },
        {
            title: t("manageAccount.downloadCentre.dataSamples"),
            field: "data-samples"
        }
    ];

    const handleDownload = (tab, index) => {
        window.open(groupedFiles[tabs[tab]?.field]?.[index]?.link, "_blank");
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.filename"),
            field: "name"
        },
        {
            type: "text",
            title: t("general.type"),
            field: "type"
        },
        {
            type: "size",
            title: t("general.size"),
            field: "size"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "download",
                    icon: <GetFileIcon />,
                    tooltip: t("general.download"),
                    onClick: index => handleDownload(value, index)
                }
            ]
        }
    ];

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("manageAccount.myAccount"), href: "/my-account" },
                    { title: t("manageAccount.downloadCentre.downloadCentre") }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">{t("manageAccount.downloadCentre.downloadCentre")}</Typography>
            </div>

            <Paper square>
                <Tabs value={value} indicatorColor="primary" textColor="primary" onChange={handleChange}>
                    {tabs.map((tab, index) => (
                        <Tab key={index} label={tab.title} />
                    ))}
                </Tabs>
                {tabs.map((tab, index) => (
                    <TabPanel value={value} key={index} index={index}>
                        <UniversalTable
                            columns={columns}
                            data={groupedFiles[tab.field]}
                            searchable
                            isLoading={isRequestingDocumentation}
                        />
                    </TabPanel>
                ))}
            </Paper>
        </CenteredContainer>
    );
};

export const DownloadCentreView = withStyles(styles)(DownloadCentreViewPure);
